import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import calibrationApi from "../api/calibrationApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";
import useGoalCycleOptions from "../hooks/useGoalCycleOptions";

const CalibrationsPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const { goalCycleOptions, isLoading: goalCyclesLoading } =
    useGoalCycleOptions();
  const [showGuide, setShowGuide] = React.useState(false);
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canReadCalibrations = can(PERFORMANCE_PERMISSIONS.CALIBRATIONS_READ);
  const canManageCalibrations = can(
    PERFORMANCE_PERMISSIONS.CALIBRATIONS_MANAGE,
  );

  if (!canReadCalibrations) {
    return (
      <ModulePageShell
        title="Calibration Sessions"
        description="Create and manage calibration sessions and finalize rating alignment."
      >
        <EmptyState message="You do not have permission to view calibration sessions." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.calibrations],
    queryFn: () => calibrationApi.listCalibrations({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: calibrationApi.createCalibration,
    onSuccess: () => {
      toast({
        title: "Calibration session created",
        status: "success",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.calibrations],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create calibration",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      cycleId: "",
      department: "",
      reviewers: "",
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="Calibration Sessions"
      description="Create and manage calibration sessions and finalize rating alignment."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canManageCalibrations ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) =>
              createMutation.mutate({
                ...values,
                reviewers: values.reviewers ? [values.reviewers] : [],
                employees: [],
              }),
            )}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Create Calibration</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Goal Cycle</FormLabel>
                  <Select
                    placeholder="Select goal cycle"
                    {...register("cycleId")}
                    isDisabled={goalCyclesLoading}
                  >
                    {goalCycleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Department</FormLabel>
                  <Input {...register("department")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Reviewer ID</FormLabel>
                  <Select
                    placeholder="Select reviewer"
                    {...register("reviewers")}
                    isDisabled={usersLoading}
                  >
                    {userOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Create Session
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to calibrations, but cannot create calibration sessions." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No calibration sessions found." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Session</Th>
                <Th>Department</Th>
                <Th>Employees</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{item._id}</Td>
                  <Td>{item.department || "-"}</Td>
                  <Td>{(item.employees || []).length}</Td>
                  <Td>{item.finalized ? "FINALIZED" : "OPEN"}</Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        navigate(`/performance/calibrations/${item._id}`)
                      }
                    >
                      Workspace
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default CalibrationsPage;
